import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  input,
  InputSignal,
  Output,
  output,
  OutputEmitterRef,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import {
  MatCell,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderRow,
  MatNoDataRow,
  MatRow,
  MatTable,
  MatTableDataSource,
  MatTableModule
} from '@angular/material/table';
import { filter, Subscription, take } from 'rxjs';
import {
  PaginationResponse,
  ReportData,
  ReportEntity,
  TableSetting,
  TableSettingRequest
} from '../../../../../../api/api.model';
import { ApiService } from '../../../../../../api/api.service';
import { SidenavService } from '../../../../../../partials/services/sidenav.service';
import { AuthService } from '../../../../../../partials/services/auth.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { DatePipe, DecimalPipe, NgClass, NgOptimizedImage, TitleCasePipe } from '@angular/common';
import { FormControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatRipple } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { CopyButtonComponent } from '../../../../../../partials/components/copy-button/copy-button.component';
import { ReportsComponent } from '../../../../../../partials/components/reports/reports.component';
import { TransformUnderscoreToSpacePipe } from '../../../../../../partials/pipes/tranform-underscore-to-space.pipe';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ReportsTableSettings {
  allDisplayedColumns: string[];
  filteredDisplayedColumns: string[];
}

// Имя таблицы необходимо для сохранения настроек в API
export const tableName = 'ReportsTable';

@Component({
  selector: 'app-reports-table',
  imports: [
    NgOptimizedImage,
    MatIconModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatFormFieldModule,
    MatRipple,
    MatPaginatorModule,
    MatNoDataRow,
    DatePipe,
    MatMenuTrigger,
    MatMenu,
    CdkDropList,
    CdkDrag,
    CdkScrollable,
    CdkDragHandle,
    MatCheckbox,
    FormsModule,
    MatTableModule,
    CopyButtonComponent,
    NgClass,
    TransformUnderscoreToSpacePipe,
    TitleCasePipe,
    MatSelectModule,
    ReactiveFormsModule,
    DecimalPipe
  ],
  templateUrl: './reports-table.component.html',
  styleUrl: './reports-table.component.scss'
})
export class ReportsTableComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private readonly _apiService: ApiService = inject(ApiService);

  private _mainSidenavService: SidenavService = inject(SidenavService);

  private readonly _authService: AuthService = inject(AuthService);

  readonly pageEvent: OutputEmitterRef<PageEvent> = output<PageEvent>();

  readonly paginationResponseData: InputSignal<PaginationResponse<Omit<ReportEntity, 'data'>>> = input.required<PaginationResponse<Omit<ReportEntity, 'data'>>>();

  @Output() outputChangeNicknameFilter: EventEmitter<string | null> = new EventEmitter<string | null>();
  readonly paginationOptions: InputSignal<{
    pageSize: number;
    pageIndex: number;
    pageSizeOptions: number[];
    length: number
  }> = input.required<{ pageSize: number; pageIndex: number; pageSizeOptions: number[]; length: number }>();

  private _viewSubscription!: Subscription;

  public markOpenRow: WritableSignal<number | null> = signal(null);

  public nicknamesList: WritableSignal<string[]> = signal([]);
  private readonly _fb: NonNullableFormBuilder = inject(NonNullableFormBuilder);
  public filterNickname: FormControl<string | null> = this._fb.control(null);

  readonly tableSettings: InputSignal<TableSetting<ReportsTableSettings> | undefined> = input<TableSetting<ReportsTableSettings> | undefined>();

  allDisplayedColumns: string[] = [
    'report_id',
    'report_type',
    'date',
    'wallet',
    'risk_score',
    'nickname',
    'service_fee',
    'review',
    'table_settings'
  ];

  filteredDisplayedColumns: string[] = [];

  get displayedColumns() {
    return this.allDisplayedColumns.filter((column) => !this.filteredDisplayedColumns.includes(column));
  };

  readonly dataSource: MatTableDataSource<Omit<ReportEntity, 'data'>> = new MatTableDataSource<Omit<ReportEntity, 'data'>>([]);

  constructor() {
    this.filterNickname.valueChanges.pipe(takeUntilDestroyed()).subscribe({
      next: (value) => {
        this.outputChangeNicknameFilter.emit(value);
      }
    });
    effect(() => {
      const { content } = this.paginationResponseData();
      this.dataSource.data = content;
    });

    effect(() => {
      const loadedTableSettings = this.tableSettings();

      if (loadedTableSettings) {
        const { data: { allDisplayedColumns, filteredDisplayedColumns } } = loadedTableSettings;
        // Проверка нужно для того чтоб если были изменения в allDisplayedColumns. Мы не перезаписывали сохраненными настройками.
        if (allDisplayedColumns.every((displayedColumn) => this.allDisplayedColumns.includes(displayedColumn))) {
          this.allDisplayedColumns = allDisplayedColumns;
          this.filteredDisplayedColumns = filteredDisplayedColumns;
        }
      }
    });
  }

  ngOnInit(): void {
    this._apiService.getWalletNicknames().pipe(take(1)).subscribe({
      next: (value: string[]) => {
        this.nicknamesList.set(value);
      }
    });
  }

  private _saveTableSettings(): void {
    const user = this._authService.accountDto();

    if (!user) return;

    const { uuid } = user;

    const body: TableSettingRequest = {
      data: {
        allDisplayedColumns: this.allDisplayedColumns,
        filteredDisplayedColumns: this.filteredDisplayedColumns,
      },
      tableName,
      userId: uuid
    };
    this._apiService.createOrUpdateTableSetting<ReportsTableSettings>(body).pipe(take(1)).subscribe({
      next: () => {
        console.log(`${ tableName } settings saved!`);
      }
    });
  }
  handlerChangeDisplayColumnCheckbox({ checked }: MatCheckboxChange, displayedColumn: string): void {
    if (checked) {
      this.filteredDisplayedColumns = this.filteredDisplayedColumns.filter((c) => c !== displayedColumn);
    } else {
      this.filteredDisplayedColumns.push(displayedColumn);
    }
    this._saveTableSettings();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.allDisplayedColumns, event.previousIndex, event.currentIndex);
    this._saveTableSettings();
  }

  viewReport(report: Omit<ReportEntity, 'data'>): void {
    this._viewSubscription?.unsubscribe();
    this._viewSubscription = this._apiService.getReportForId(report.id).pipe(
      filter(Boolean)
    ).subscribe({
      next: (report: ReportEntity<ReportData>) => {
        this.markOpenRow.set(report.id);
        let inputs = {
          report
        };
        this._mainSidenavService.open<boolean>({
          template: ReportsComponent,
          inputs
        }).afterClosed.pipe(take(1)).subscribe({
          next: () => {
            this.markOpenRow.set(null);
          }
        })
      }
    });
  }
}
